import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Send, Zap, Loader2, MessageSquare, Sparkles, ChevronRight, Users, Layers, AlertCircle, CheckCircle, FileText, Plus } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';
import { DebateConfig } from '../components/DebateConfig';
import { ContextAttacher } from '../components/ContextAttacher';

export default function PromptBuilder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Live spec state (extracted from SPEC_STATE in AI responses)
  const [spec, setSpec] = useState(null);
  const [debateMode, setDebateMode] = useState('panel');
  const [frictionLevel, setFrictionLevel] = useState(5);
  const [contextIds, setContextIds] = useState([]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Extract SPEC_STATE from AI responses
  const extractSpecState = (text) => {
    const match = text.match(/<!--\s*SPEC_STATE\s*\n([\s\S]*?)\n\s*-->/);
    if (match) {
      try {
        const parsed = JSON.parse(match[1]);
        setSpec(parsed);
        return text.replace(/<!--\s*SPEC_STATE[\s\S]*?-->/, '').trim();
      } catch {}
    }
    return text;
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    const userMsg = { role: 'user', content: input.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);
    setError('');
    try {
      // Load context items for context-aware building
      let contextItems = [];
      if (contextIds.length > 0) {
        for (const cid of contextIds) {
          try {
            const r = await API.get(`/api/contexts/${cid}`);
            contextItems.push({ title: r.data.title, type: r.data.type, snippet: r.data.content?.slice(0, 200) });
          } catch {}
        }
      }
      const res = await API.post('/api/prompt-builder/chat', { message: userMsg.content, history: messages, context_items: contextItems });
      const cleaned = extractSpecState(res.data.response);
      setMessages(prev => [...prev, { role: 'assistant', content: cleaned }]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to get response');
    }
    setSending(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleBuildFray = async () => {
    if (!spec || (spec.voices || []).length < 2) {
      setError('Need at least 2 voices in the spec. Keep chatting to develop the committee.');
      return;
    }
    setExtracting(true);
    try {
      const res = await API.post('/api/frays', {
        scenario: spec.scenario || '',
        num_phases: (spec.phases || []).length || 3,
        debate_mode: debateMode,
        friction_level: frictionLevel,
        context_ids: contextIds,
        custom_voices: (spec.voices || []).map((v, i) => ({
          name: v.name, mandate: v.mandate, model: v.model_hint || 'google/gemini-2.5-flash',
          color: ['#3B82F6', '#EF4444', '#22C55E', '#A855F7', '#F59E0B', '#06B6D4', '#EC4899'][i % 7],
        })),
      });
      navigate(`/fray/${res.data.id}/reveal`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create Fray');
    }
    setExtracting(false);
  };

  const STARTERS = [
    "I need to stress-test a business idea for a SaaS startup",
    "Help me design a committee to evaluate a marketing strategy",
    "I want to debate a technical architecture decision",
    "Set up a committee for investment due diligence",
  ];

  // Quality indicators
  const quality = spec?.quality || {};
  const voicesDefined = (spec?.voices || []).length;
  const phasesDefined = (spec?.phases || []).length;
  const isReady = voicesDefined >= 2 && phasesDefined >= 1 && spec?.scenario;

  return (
    <div className="h-screen bg-[#0F0F0F] flex flex-col">
      <Header />
      <div className="flex-1 flex pt-14 overflow-hidden">
        {/* LEFT PANEL: Chat */}
        <div className="flex-1 flex flex-col min-w-0 border-r border-[#2A2A2A]">
          <div className="border-b border-[#2A2A2A] px-4 py-2 shrink-0">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#FF4D00]" />
              <h1 className="text-sm font-semibold font-['Barlow_Condensed']" data-testid="builder-title">Prompt Builder</h1>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3">
            {messages.length === 0 && (
              <div className="py-12">
                <div className="text-center mb-6">
                  <MessageSquare className="w-8 h-8 text-[#2A2A2A] mx-auto mb-3" />
                  <h2 className="text-lg font-bold font-['Barlow_Condensed'] mb-1">Design Your Committee</h2>
                  <p className="text-xs text-[#A0A0A0]">Describe your scenario and I'll help design the debate</p>
                </div>
                <div className="grid grid-cols-2 gap-2" data-testid="starters">
                  {STARTERS.map((s, i) => (
                    <button key={i} onClick={() => setInput(s)}
                      className="p-2.5 bg-[#1A1A1A] border border-[#2A2A2A] text-left text-xs text-[#A0A0A0] hover:border-[#FF4D00]/40 hover:text-[#F5F5F5]" data-testid={`starter-${i}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className={`mb-3 ${msg.role === 'user' ? 'flex justify-end' : ''}`}>
                {msg.role === 'user' ? (
                  <div className="max-w-[85%] p-3 bg-[#FF4D00]/10 border border-[#FF4D00]/20">
                    <p className="text-xs text-[#F5F5F5] whitespace-pre-wrap">{msg.content}</p>
                  </div>
                ) : (
                  <div className="max-w-[95%] p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Sparkles className="w-3 h-3 text-[#FF4D00]" />
                      <span className="text-[9px] font-mono text-[#FF4D00]">DESIGNER</span>
                    </div>
                    <div className="text-xs text-[#F5F5F5] whitespace-pre-wrap leading-relaxed">{msg.content}</div>
                  </div>
                )}
              </motion.div>
            ))}
            {sending && (
              <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] max-w-[90%] mb-3">
                <Loader2 className="w-3.5 h-3.5 text-[#FF4D00] animate-spin" />
              </div>
            )}
            {error && <div className="p-2 mb-3 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-xs">{error}</div>}
            <div ref={messagesEndRef} />
          </div>

          <div className="border-t border-[#2A2A2A] px-4 py-2 shrink-0">
            <form onSubmit={sendMessage} className="flex gap-2">
              <input ref={inputRef} type="text" value={input} onChange={e => setInput(e.target.value)}
                className="flex-1 px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-xs focus:outline-none focus:border-[#FF4D00]"
                placeholder="Describe your scenario..." disabled={sending} data-testid="builder-input" />
              <button type="submit" disabled={sending || !input.trim()}
                className="px-3 py-2 bg-[#FF4D00] text-black disabled:opacity-40" data-testid="builder-send">
                <Send className="w-3.5 h-3.5" />
              </button>
            </form>
          </div>
        </div>

        {/* RIGHT PANEL: Live Spec Preview + Config */}
        <div className="w-96 flex flex-col shrink-0 overflow-y-auto bg-[#0A0A0A]">
          <div className="border-b border-[#2A2A2A] px-4 py-2 shrink-0">
            <span className="text-xs font-mono text-[#A0A0A0]">LIVE SPEC</span>
          </div>

          <div className="flex-1 p-4 space-y-4">
            {/* Quality Indicators */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[9px] px-1.5 py-0.5 border font-mono ${voicesDefined >= 2 ? 'text-[#22C55E] border-[#22C55E]/30' : 'text-[#A0A0A0] border-[#2A2A2A]'}`}>
                <Users className="w-3 h-3 inline mr-0.5" />{voicesDefined} voices
              </span>
              <span className={`text-[9px] px-1.5 py-0.5 border font-mono ${phasesDefined >= 1 ? 'text-[#22C55E] border-[#22C55E]/30' : 'text-[#A0A0A0] border-[#2A2A2A]'}`}>
                <Layers className="w-3 h-3 inline mr-0.5" />{phasesDefined} phases
              </span>
              {quality.has_adversary && <span className="text-[9px] px-1.5 py-0.5 bg-[#FF4D00]/10 text-[#FF4D00] border border-[#FF4D00]/20 font-mono">Adversary</span>}
              {isReady ? (
                <span className="text-[9px] px-1.5 py-0.5 bg-[#22C55E]/10 text-[#22C55E] border border-[#22C55E]/20 font-mono"><CheckCircle className="w-3 h-3 inline mr-0.5" />Ready</span>
              ) : (
                <span className="text-[9px] px-1.5 py-0.5 text-[#A0A0A0] border border-[#2A2A2A] font-mono"><AlertCircle className="w-3 h-3 inline mr-0.5" />Developing...</span>
              )}
            </div>

            {/* Scenario */}
            {spec?.scenario && (
              <div>
                <span className="text-[9px] font-mono text-[#A0A0A0] block mb-1">SCENARIO</span>
                <p className="text-xs text-[#F5F5F5] leading-relaxed bg-[#1A1A1A] p-2 border border-[#2A2A2A]">{spec.scenario.slice(0, 200)}{spec.scenario.length > 200 ? '...' : ''}</p>
              </div>
            )}

            {/* Voices */}
            {(spec?.voices || []).length > 0 && (
              <div>
                <span className="text-[9px] font-mono text-[#A0A0A0] block mb-1">COMMITTEE ({voicesDefined})</span>
                <div className="space-y-1.5">
                  {spec.voices.map((v, i) => (
                    <div key={i} className="p-2 bg-[#1A1A1A] border border-[#2A2A2A]">
                      <div className="flex items-center gap-2 mb-0.5">
                        <div className="w-4 h-4 border text-[7px] font-bold flex items-center justify-center"
                          style={{ borderColor: ['#3B82F6','#EF4444','#22C55E','#A855F7','#F59E0B'][i%5], color: ['#3B82F6','#EF4444','#22C55E','#A855F7','#F59E0B'][i%5] }}>
                          {(v.name || '?').charAt(0)}
                        </div>
                        <span className="text-xs font-medium font-['Barlow_Condensed']">{v.name}</span>
                        {v.model_hint && <span className="text-[8px] font-mono text-[#A0A0A0] ml-auto">{v.model_hint}</span>}
                      </div>
                      <p className="text-[9px] text-[#A0A0A0] leading-relaxed">{v.mandate?.slice(0, 100)}{v.mandate?.length > 100 ? '...' : ''}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Phases */}
            {(spec?.phases || []).length > 0 && (
              <div>
                <span className="text-[9px] font-mono text-[#A0A0A0] block mb-1">PHASES ({phasesDefined})</span>
                <div className="space-y-1">
                  {spec.phases.map((p, i) => (
                    <div key={i} className="flex items-start gap-2 px-2 py-1 border-l-2" style={{ borderColor: i === spec.phases.length - 1 ? '#FF4D00' : '#2A2A2A' }}>
                      <span className="text-[8px] font-mono text-[#A0A0A0] shrink-0">{String(i+1).padStart(2,'0')}</span>
                      <span className="text-[10px]">{p.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Issues */}
            {quality.issues?.length > 0 && (
              <div>
                <span className="text-[9px] font-mono text-[#EF4444] block mb-1">ISSUES</span>
                {quality.issues.map((issue, i) => (
                  <p key={i} className="text-[9px] text-[#EF4444] flex items-start gap-1"><AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />{issue}</p>
                ))}
              </div>
            )}

            {/* No spec yet */}
            {!spec && messages.length === 0 && (
              <div className="text-center py-8">
                <Sparkles className="w-6 h-6 text-[#2A2A2A] mx-auto mb-2" />
                <p className="text-xs text-[#A0A0A0]">Spec will populate as you design</p>
              </div>
            )}

            <div className="border-t border-[#2A2A2A] pt-4">
              {/* Debate Config */}
              <DebateConfig debateMode={debateMode} setDebateMode={setDebateMode} frictionLevel={frictionLevel} setFrictionLevel={setFrictionLevel} />

              {/* Context */}
              <ContextAttacher selectedIds={contextIds} onChange={setContextIds} />
            </div>
          </div>

          {/* Build Button */}
          <div className="border-t border-[#2A2A2A] p-4 shrink-0">
            <button onClick={handleBuildFray} disabled={extracting || !isReady}
              className="w-full py-3 bg-[#FF4D00] text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-40 hover:bg-[#FF4D00]/90"
              data-testid="build-fray-btn">
              {extracting ? <><Loader2 className="w-4 h-4 animate-spin" /> Building...</> : <><Zap className="w-4 h-4" /> Build Fray</>}
            </button>
            {!isReady && messages.length > 0 && (
              <p className="text-[9px] text-[#A0A0A0] text-center mt-2">Need: {voicesDefined < 2 ? `${2 - voicesDefined} more voice(s)` : ''}{!spec?.scenario ? ' + scenario' : ''}{phasesDefined < 1 ? ' + phases' : ''}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
